/**
 * Tâche 1318 (Eric 2026-06-30) — Bandeau rouge « clé IA refusée », en haut de
 * toutes les pages.
 *
 * Une clé refusée ne se voyait que sur la page « Sentinelles » (fondateur) ou
 * dans un courriel : pendant ce temps, un concepteur relançait des générations
 * qui échouaient sans comprendre pourquoi. Le bandeau, lui, se voit forcément.
 *
 * Périmètre voulu par Eric : concepteur, admin, fondateur. Un fondateur voit
 * aussi les organisations des autres clients (c'est lui qui va faire renouveler
 * la clé) ; les autres ne voient que la leur. La route répond 403 aux autres
 * rôles, donc le bandeau n'apparaît simplement pas.
 *
 * Masquage : **1 heure** (choix d'Eric). Plus court que le bandeau d'incident
 * silencieux (4 h) parce qu'une clé refusée bloque la production de contenu à
 * la minute. Et le masquage tombe dès qu'une AUTRE clé lâche : on peut vouloir
 * travailler en paix sur un problème connu, jamais rater le suivant.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Key, X } from "@phosphor-icons/react";
import { useAuth } from "../../contexts/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;
const DISMISS_KEY = "knoria.aiKeyFailureBanner.dismissed";
const DISMISS_HOURS = 1;

const signatureOf = (state) =>
  (state?.keys || [])
    .map((k) => `${k.org_id || ""}:${k.service || ""}`)
    .sort()
    .join("|") || String(state?.count || 0);

const readDismissal = () => {
  try {
    return JSON.parse(window.localStorage.getItem(DISMISS_KEY) || "null");
  } catch {
    return null;
  }
};

// Tâche 1320 (Eric 2026-06-30) — L'ancienneté de l'échec, pour savoir quelle
// clé relancer en premier. Le formatage vit dans `lib/duree.js` (testable sans
// tirer axios derrière lui).
import { durerEnClair, SEUIL_ALERTE_SECONDES } from "../../lib/duree";

const heureLocale = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString("fr-CA", {
      day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return null;
  }
};

export const AiKeyFailureBanner = () => {
  const { user } = useAuth();
  const [state, setState] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // On attend de savoir QUI regarde. Interroger la route avant que la
    // session soit établie renvoyait 401, et le bandeau ne revenait jamais :
    // sur un rechargement à froid, la clé refusée passait inaperçue — c'est
    // exactement ce que la tâche 1318 devait empêcher.
    if (!user) return undefined;
    let cancelled = false;
    axios
      .get(`${API}/api/org/ai-package/failing-keys`, { withCredentials: true })
      .then((res) => {
        if (cancelled) return;
        setState(res.data);
        const saved = readDismissal();
        const stillFresh =
          saved &&
          saved.signature === signatureOf(res.data) &&
          Date.now() - Number(saved.at || 0) < DISMISS_HOURS * 3600 * 1000;
        setDismissed(Boolean(stillFresh));
      })
      // 403 pour un apprenant : le bandeau n'est simplement pas affiché.
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [user]);

  const dismiss = () => {
    try {
      window.localStorage.setItem(
        DISMISS_KEY,
        JSON.stringify({ at: Date.now(), signature: signatureOf(state) })
      );
    } catch {
      // Stockage refusé (navigation privée) : le masquage ne durera que le
      // temps de la page. Mieux que de ne rien masquer du tout.
    }
    setDismissed(true);
  };

  if (!state?.count || dismissed) return null;

  const premiere = state.keys?.[0];
  const autres = Math.max(0, (state.count || 0) - 1);
  const constat = heureLocale(premiere?.tested_at);
  // Tâche 1320 — L'ancienneté de l'échec, pour savoir quoi relancer d'abord.
  const anciennete = durerEnClair(premiere?.failing_seconds);
  const urgente = (premiere?.failing_seconds || 0) >= SEUIL_ALERTE_SECONDES;
  const essais = Number(premiere?.fail_streak || 0);

  return (
    <div
      className="mb-3 flex items-start gap-3 rounded-xl border border-red-300 bg-red-50 px-4 py-2.5"
      data-testid="ai-key-failure-banner"
    >
      <Key size={18} className="mt-0.5 shrink-0 text-red-600" />
      <div className="flex-1 text-sm text-red-900" data-testid="ai-key-failure-banner-text">
        <strong>
          {/* Tâche 1494 — Le libellé lisible vient du serveur (une seule
              définition, `SUPPORTED_SERVICES`). « Clé embeddings refusée »
              ne disait rien à personne. */}
          Clé {premiere?.service_label || premiere?.service} refusée
          {premiere?.org_name ? ` — ${premiere.org_name}` : ""}.
        </strong>{" "}
        {anciennete && (
          <span
            className={urgente ? "font-bold text-red-800" : "text-red-800"}
            data-testid="ai-key-failure-banner-age"
          >
            En échec depuis {premiere?.failing_since_approximate ? "au moins " : ""}
            {anciennete}
            {essais > 1 ? ` (${essais} essais infructueux)` : ""}.{" "}
          </span>
        )}
        {premiere?.message}{" "}
        {constat && (
          <span className="text-red-700" data-testid="ai-key-failure-banner-when">
            Constaté le {constat}
            {premiere?.stale ? " (aucun test depuis)" : ""}.{" "}
          </span>
        )}
        {autres > 0 && (
          <span data-testid="ai-key-failure-banner-others">
            {autres} autre(s) clé(s) en échec :{" "}
            {(state.keys || []).slice(1, 5).map((k, i) => (
              <span key={`${k.org_id}-${k.service}`}>
                {i > 0 ? " · " : ""}
                {k.service_label || k.service}
                {k.org_name ? ` (${k.org_name}` : ""}
                {durerEnClair(k.failing_seconds) ? `, ${durerEnClair(k.failing_seconds)}` : ""}
                {k.org_name ? ")" : ""}
              </span>
            ))}
            {autres > 4 ? ` … et ${autres - 4} de plus` : ""}.{" "}
          </span>
        )}
        La génération de contenu qui dépend de cette clé échouera tant qu'elle
        n'est pas remplacée.{" "}
        {state.can_open_settings ? (
          <a
            href="/dashboard/settings/ai-package"
            className="font-semibold underline"
            data-testid="ai-key-failure-banner-link"
          >
            Ouvrir la configuration du Forfait AI
          </a>
        ) : (
          <span data-testid="ai-key-failure-banner-tell-admin">
            Prévenez un administrateur : la clé se remplace dans la
            configuration du Forfait AI.
          </span>
        )}
      </div>
      <button
        type="button"
        onClick={dismiss}
        className="shrink-0 p-1 rounded text-red-700 hover:bg-red-100"
        title={`Masquer ${DISMISS_HOURS} h (revient si une autre clé lâche)`}
        aria-label="Masquer ce bandeau"
        data-testid="ai-key-failure-banner-dismiss"
      >
        <X size={16} />
      </button>
    </div>
  );
};

export default AiKeyFailureBanner;
